import { useState, ReactNode } from 'react';
import {
  FileText,
  ShieldAlert,
  Loader2,
  CheckCircle2,
  Ban,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import {
  useInvoiceDetailQuery,
  useInvoiceListQuery,
} from '@/client/api/payment/payment.query';
import {
  useCancelInvoiceMutation,
  useFinalizeInvoiceMutation,
  useMarkInvoicePaidMutation,
  useRefundInvoiceMutation,
  useVoidInvoiceMutation,
} from '@/client/api/payment/payment.mutation';
import {
  formatMoney,
  Invoice,
  InvoiceListParams,
  InvoiceStatus,
  paymentEntityLink,
} from '@/client/types/payment';
import { usePaymentListUrlState } from '@/client/hooks/usePaymentListUrlState';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import Pagination from '@/client/components/common/Pagination';
import { generateIdempotencyKey } from '@/client/utils/requestSecurity';
import { PaymentsFilterBar, SortableTh } from '@/client/components/admin/Payment/PaymentsFilterBar';
import {
  EntityRelationshipLink,
  OrgRelationshipLink,
  RelationshipLink,
  UserRelationshipLink,
} from '@/client/components/admin/Payment/RelationshipLink';

import { ExportCsvButton } from '@/client/components/admin/Payment/ExportCsvButton';
const STATUS_BADGE: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  pending: 'bg-yellow-100 text-yellow-800',
  paid: 'bg-green-100 text-green-800',
  overdue: 'bg-orange-100 text-orange-800',
  canceled: 'bg-gray-100 text-gray-600',
  voided: 'bg-red-100 text-red-800',
  refunded: 'bg-purple-100 text-purple-800',
};

const STATUS_OPTIONS = (
  ['draft', 'pending', 'paid', 'overdue', 'canceled', 'voided', 'refunded'] as const
).map((s) => ({ value: s, label: s }));

const BILLING_TYPE_OPTIONS = (
  ['one_time', 'recurring', 'plan_renewal', 'order'] as const
).map((t) => ({ value: t, label: t }));

const formatDate = (raw?: string | null) => {
  if (!raw) return '—';
  try {
    return new Date(raw).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return raw;
  }
};

const AccessDenied = () => (
  <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-gray-100 bg-white py-20 text-center shadow-sm">
    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
      <ShieldAlert className="h-7 w-7 text-red-500" />
    </div>
    <h2 className="text-lg font-semibold text-gray-800">Access restricted</h2>
    <p className="max-w-sm text-sm text-gray-500">
      Viewing invoices requires the{' '}
      <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">payment.view</code> permission.
    </p>
  </div>
);

const InfoRow = ({ label, value }: { label: string; value: ReactNode }) => (
  <div>
    <p className="mb-0.5 text-xs text-gray-500">{label}</p>
    <div className="text-sm font-medium text-gray-900">{value ?? '—'}</div>
  </div>
);

const InvoiceDetailsDrawer = ({
  invid,
  isOpen,
  setIsOpen,
  canEdit,
  canRefund,
  canVoid,
  onClose,
}: {
  invid: number | null;
  isOpen: boolean;
  setIsOpen: (o: boolean) => void;
  canEdit: boolean;
  canRefund: boolean;
  canVoid: boolean;
  onClose: () => void;
}) => {
  const [note, setNote] = useState('');
  const [showRefund, setShowRefund] = useState(false);
  const [refundReason, setRefundReason] = useState('');

  const { data: invoice, isLoading } = useInvoiceDetailQuery(isOpen ? invid : null);
  const markPaid = useMarkInvoicePaidMutation();
  const finalize = useFinalizeInvoiceMutation();
  const voidInv = useVoidInvoiceMutation();
  const cancelInv = useCancelInvoiceMutation();
  const refundInv = useRefundInvoiceMutation();

  const busy =
    markPaid.isPending ||
    finalize.isPending ||
    voidInv.isPending ||
    cancelInv.isPending ||
    refundInv.isPending;

  if (!isOpen) return null;

  const status = (invoice?.status || '').toString();
  const entityLink = invoice ? paymentEntityLink(invoice) : null;
  const entitySummary =
    invoice?.entity_summary ||
    (entityLink
      ? {
          eid: entityLink.eid,
          name: entityLink.name,
          slug: entityLink.slug,
          ffid: entityLink.ffid,
          uuid: entityLink.uuid,
        }
      : null);

  const run = async (label: string, fn: () => Promise<unknown>) => {
    try {
      await fn();
      toast.success(label, { richColors: true });
      setNote('');
      setShowRefund(false);
      setRefundReason('');
    } catch (error: any) {
      toast.error(error?.message || `Failed: ${label}`, { richColors: true });
    }
  };

  return (
    <Drawer open={isOpen} onOpenChange={setIsOpen}>
      <DrawerContent className="mx-auto flex max-h-[92vh] max-w-2xl flex-col">
        <DrawerHeader className="border-b border-gray-100 text-left">
          <DrawerTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600" />
            {isLoading ? 'Loading…' : `Invoice #${invoice?.invid ?? invid}`}
          </DrawerTitle>
          <DrawerDescription>
            {invoice?.billing_type ? String(invoice.billing_type) : 'Invoice details'}
          </DrawerDescription>
        </DrawerHeader>

        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          {isLoading && (
            <div className="space-y-3">
              <Skeleton className="h-8 w-40" />
              <Skeleton className="h-24 w-full" />
            </div>
          )}
          {!isLoading && invoice && (
            <>
              <span
                className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                  STATUS_BADGE[status] || STATUS_BADGE.draft
                }`}
              >
                {status.toUpperCase() || 'UNKNOWN'}
              </span>
              <div className="rounded-lg bg-gray-50 p-4">
                <p className="text-xs text-gray-500">Amount</p>
                <p className="text-2xl font-black text-gray-900">
                  {formatMoney(invoice.amount, invoice.ccode)}
                </p>
                {invoice.amount_due != null && (
                  <p className="mt-1 text-sm text-gray-500">
                    Due: {formatMoney(invoice.amount_due, invoice.ccode)}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <InfoRow label="INVID" value={invoice.invid} />
                <InfoRow label="Billing type" value={invoice.billing_type || '—'} />
                <InfoRow label="User" value={<UserRelationshipLink uid={invoice.uid} summary={invoice.user_summary} />} />
                <InfoRow label="Org" value={<OrgRelationshipLink oid={invoice.oid} summary={invoice.org_summary} />} />
                <InfoRow
                  label="Plan"
                  value={
                    invoice.plid != null ? (
                      <RelationshipLink kind="subscription" id={invoice.plid} label={`Plan #${invoice.plid}`} />
                    ) : (
                      '—'
                    )
                  }
                />
                <InfoRow label="Added" value={formatDate(invoice.date_added)} />
                <InfoRow label="Due" value={formatDate(invoice.date_due)} />
                <InfoRow label="Paid" value={formatDate(invoice.date_paid)} />
              </div>
              {entitySummary && (
                <div className="rounded-lg border border-gray-100 p-4">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Linked entity
                  </p>
                  <EntityRelationshipLink eid={entitySummary.eid} summary={entitySummary} />
                </div>
              )}
              {(canVoid || showRefund) && (
                <div className="space-y-1.5">
                  <Label>Note (optional)</Label>
                  <Input value={note} onChange={(e) => setNote(e.target.value)} />
                </div>
              )}
              {showRefund && canRefund && (
                <div className="space-y-1.5 rounded-lg border border-red-100 bg-red-50 p-3">
                  <Label>Refund reason</Label>
                  <Input
                    value={refundReason}
                    onChange={(e) => setRefundReason(e.target.value)}
                    placeholder="Reason for refund"
                  />
                </div>
              )}
            </>
          )}
        </div>

        <DrawerFooter className="border-t border-gray-100">
          <div className="flex flex-wrap gap-2">
            {canEdit && invoice && status === 'draft' && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() => run('Invoice finalized', () => finalize.mutateAsync(invoice.invid))}
              >
                {finalize.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                Finalize
              </Button>
            )}
            {canEdit && invoice && status !== 'paid' && status !== 'voided' && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() => run('Marked paid', () => markPaid.mutateAsync(invoice.invid))}
              >
                {markPaid.isPending ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="mr-1.5 h-4 w-4" />
                )}
                Mark paid
              </Button>
            )}
            {canVoid && invoice && status !== 'voided' && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() =>
                  run('Invoice voided', () =>
                    voidInv.mutateAsync({ invid: invoice.invid, note: note || undefined }),
                  )
                }
              >
                {voidInv.isPending ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <Ban className="mr-1.5 h-4 w-4" />
                )}
                Void
              </Button>
            )}
            {canVoid && invoice && status !== 'canceled' && status !== 'voided' && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() =>
                  run('Invoice canceled', () =>
                    cancelInv.mutateAsync({ invid: invoice.invid, note: note || undefined }),
                  )
                }
              >
                Cancel
              </Button>
            )}
            {canRefund && invoice && status === 'paid' && (
              <Button
                type="button"
                variant={showRefund ? 'destructive' : 'outline'}
                size="sm"
                disabled={busy}
                onClick={() => {
                  if (!showRefund) {
                    setShowRefund(true);
                    return;
                  }
                  if (!refundReason.trim()) {
                    toast.error('Refund reason required', { richColors: true });
                    return;
                  }
                  void run('Invoice refunded', () =>
                    refundInv.mutateAsync({
                      invid: invoice.invid,
                      body: { reason: refundReason.trim() },
                      idempotencyKey: generateIdempotencyKey(),
                    }),
                  );
                }}
              >
                {refundInv.isPending ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-1.5 h-4 w-4" />
                )}
                {showRefund ? 'Confirm refund' : 'Refund'}
              </Button>
            )}
            <DrawerClose asChild>
              <Button type="button" variant="ghost" size="sm" onClick={onClose}>
                Close
              </Button>
            </DrawerClose>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

const InvoiceListPage = () => {
  useBreadcrumbs([
    { label: 'Payments', href: '/admin/payments' },
    { label: 'Invoices' },
  ]);

  const viewPerm = useHasPermission('payment.view');
  const editPerm = useHasPermission('payment.edit');
  const refundPerm = useHasPermission('payment.refund');
  const voidPerm = useHasPermission('payment.void');

  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const canEdit = editPerm.isSuperAdmin || editPerm.hasPermission;
  const canRefund = refundPerm.isSuperAdmin || refundPerm.hasPermission;
  const canVoid = voidPerm.isSuperAdmin || voidPerm.hasPermission;
  const permLoading = viewPerm.isLoading;

  const { state, offset, patch, clearFilters, setDetail, toggleSort, toApiNumber } =
    usePaymentListUrlState();

  const listParams: InvoiceListParams = {
    limit: state.limit,
    offset,
    ...(state.q.trim() ? { q: state.q.trim() } : {}),
    ...(state.status ? { status: state.status as InvoiceStatus } : {}),
    ...(state.billing_type ? { billing_type: state.billing_type as InvoiceListParams['billing_type'] } : {}),
    ...(state.uid ? { uid: toApiNumber(state.uid) } : {}),
    ...(state.oid ? { oid: toApiNumber(state.oid) } : {}),
    ...(state.ccode ? { ccode: state.ccode } : {}),
    ...(state.date_from ? { date_from: state.date_from } : {}),
    ...(state.date_to ? { date_to: state.date_to } : {}),
    ...(state.amount_min ? { amount_min: toApiNumber(state.amount_min) } : {}),
    ...(state.amount_max ? { amount_max: toApiNumber(state.amount_max) } : {}),
    ...(state.sort_by ? { sort_by: state.sort_by, order: state.order || 'desc' } : {}),
  };

  const { data, isLoading, isError, error } = useInvoiceListQuery(
    listParams,
    canView && !permLoading,
  );

  const invoices = data?.invoices ?? [];
  const pagination = data?.pagination;
  const forbidden =
    (error as { status?: number })?.status === 403 || (!permLoading && !canView);

  if (permLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl space-y-4">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl">
          <AccessDenied />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-screen-2xl space-y-6">
        <div>
          <div className="mb-1 flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
              <FileText className="h-4 w-4 text-white" />
            </div>
            <h1 className="text-2xl font-black text-gray-900">Invoices</h1>
          </div>
          <p className="text-sm text-gray-500">Finalize, mark paid, void, cancel, or refund.</p>
          <div className="mt-3">
            <ExportCsvButton path="/api/admin/v2/invoices" params={listParams} filename="invoices.csv" />
          </div>
        </div>

        <PaymentsFilterBar
          state={state}
          onPatch={patch}
          onClear={() => clearFilters()}
          statusOptions={STATUS_OPTIONS}
          billingTypeOptions={BILLING_TYPE_OPTIONS}
          showAmount
          showDates
          showOid
          showCcode
          searchPlaceholder="Search ffid, uuid, or id…"
        />

        {isError && !forbidden && (
          <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
            {(error as Error)?.message || 'Failed to load invoices'}
          </div>
        )}

        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    INVID
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    User
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Org
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Entity
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Plan
                  </th>
                  <SortableTh
                    label="Amount"
                    field="amount"
                    sortBy={state.sort_by}
                    order={state.order}
                    onToggle={toggleSort}
                  />
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Status
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Billing
                  </th>
                  <SortableTh
                    label="Date"
                    field="date_added"
                    sortBy={state.sort_by}
                    order={state.order}
                    onToggle={toggleSort}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {isLoading &&
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 9 }).map((__, j) => (
                        <td key={j} className="px-6 py-4">
                          <Skeleton className="h-4 w-16" />
                        </td>
                      ))}
                    </tr>
                  ))}
                {!isLoading && invoices.length === 0 && (
                  <tr>
                    <td colSpan={9} className="py-16 text-center text-sm text-gray-500">
                      No invoices found
                    </td>
                  </tr>
                )}
                {!isLoading &&
                  invoices.map((inv: Invoice) => {
                    const entityLink = paymentEntityLink(inv);
                    const entitySummary =
                      inv.entity_summary ||
                      (entityLink
                        ? {
                            eid: entityLink.eid,
                            name: entityLink.name,
                            slug: entityLink.slug,
                            ffid: entityLink.ffid,
                            uuid: entityLink.uuid,
                          }
                        : null);
                    return (
                      <tr
                        key={inv.invid}
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => setDetail('invid', inv.invid)}
                      >
                        <td className="px-6 py-4 font-mono text-sm font-semibold">
                          #{inv.invid}
                          {inv.ffid ? (
                            <p className="text-xs font-normal text-gray-400">{inv.ffid}</p>
                          ) : null}
                        </td>
                        <td className="max-w-[160px] px-6 py-4">
                          <UserRelationshipLink uid={inv.uid} summary={inv.user_summary} />
                        </td>
                        <td className="max-w-[140px] px-6 py-4">
                          <OrgRelationshipLink oid={inv.oid} summary={inv.org_summary} />
                        </td>
                        <td className="max-w-[160px] px-6 py-4">
                          <EntityRelationshipLink eid={entitySummary?.eid ?? null} summary={entitySummary} />
                        </td>
                        <td className="px-6 py-4">
                          {inv.plid != null ? (
                            <RelationshipLink kind="subscription" id={inv.plid} label={`#${inv.plid}`} />
                          ) : (
                            <span className="text-sm text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm font-bold">
                          {formatMoney(inv.amount, inv.ccode)}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                              STATUS_BADGE[String(inv.status)] || STATUS_BADGE.draft
                            }`}
                          >
                            {String(inv.status || '—').toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">{inv.billing_type || '—'}</td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {formatDate(inv.date_added)}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          <div className="border-t border-gray-100 px-6 py-3">
            <Pagination
              total={pagination?.total ?? 0}
              page={state.page}
              itemsPerPage={state.limit}
              limit={state.limit}
              setPage={(page) => patch({ page }, false)}
              setItemsPerPage={(limit) => patch({ limit, page: 1 }, false)}
              showButtons
              showSelect
              selectOptions={[10, 25, 50, 100]}
            />
          </div>
        </div>
      </div>

      <InvoiceDetailsDrawer
        invid={state.invid}
        isOpen={state.invid != null}
        setIsOpen={(open) => {
          if (!open) setDetail('invid', null);
        }}
        canEdit={canEdit}
        canRefund={canRefund}
        canVoid={canVoid}
        onClose={() => setDetail('invid', null)}
      />
    </div>
  );
};

export default InvoiceListPage;
